/**
 * Modal shown when the app is running as an installed PWA (standalone) and
 * notification permission is not yet granted. Triggers the native "enable
 * notifications" flow so the user is prompted to allow. Shown once per session;
 * we keep asking again on next session until they enable or deny.
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Bell } from 'lucide-react';
import { Button } from '../ui/Button';
import {
  isPushSupported,
  getPermission,
  getStoredPushChoice,
  setStoredPushChoice,
  enablePushNotifications,
  enableTenantPushNotifications,
} from '../../services/pushNotificationService';
import { useToast } from '../ui/Toaster';

const SESSION_KEY = 'tenantplus_notification_modal_shown_standalone';

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1090;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const Card = styled.div`
  background: ${(p) => p.theme.card};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 16px;
  padding: 24px;
  max-width: 380px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
`;

const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: ${(p) => p.theme.primary};
  color: ${(p) => p.theme.primaryForeground};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  svg {
    width: 28px;
    height: 28px;
  }
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${(p) => p.theme.foreground};
  margin: 0 0 8px 0;
`;

const Body = styled.p`
  font-size: 0.9375rem;
  color: ${(p) => p.theme.mutedForeground};
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

interface EnableNotificationsModalProps {
  /** When set, use tenant push flow; otherwise landlord. */
  tenantId?: string;
  /** When true, app is running as installed PWA (standalone). */
  isStandalone: boolean;
}

export function EnableNotificationsModal({ tenantId, isStandalone }: EnableNotificationsModalProps) {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();

  // When PWA is installed (standalone): show modal every time until they enable or deny (keep asking).
  useEffect(() => {
    if (!isStandalone || !isPushSupported()) {
      setVisible(false);
      return;
    }
    const permission = getPermission();
    const stored = getStoredPushChoice();
    if (stored === 'denied' || permission === 'granted') {
      setVisible(false);
      return;
    }
    // Only hide for this session after they successfully enabled (SESSION_KEY set in handleEnable).
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') {
        setVisible(false);
        return;
      }
    } catch {
      // ignore
    }
    setVisible(true);
  }, [isStandalone]);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const ok = tenantId
        ? await enableTenantPushNotifications(tenantId)
        : await enablePushNotifications();
      if (ok) {
        setStoredPushChoice('granted');
        setVisible(false);
        try {
          sessionStorage.setItem(SESSION_KEY, '1');
        } catch {
          // ignore
        }
        toastSuccess('Notifications enabled', {
          description: tenantId
            ? "You'll receive rent reminders and lease updates."
            : "You'll receive payment and trial reminders.",
        });
      } else {
        const perm = getPermission();
        if (perm === 'denied') {
          setVisible(false);
          toastError('Notifications blocked', {
            description: 'You can enable them later in your browser settings.',
          });
        } else {
          toastError('Could not enable notifications', {
            description: 'You can try again from Settings.',
          });
        }
      }
    } catch (e) {
      toastError('Something went wrong', { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    // Do NOT set SESSION_KEY so we show again on next PWA open (keep asking until they enable or deny)
  };

  if (!visible) return null;

  return (
    <Overlay $open={visible} onClick={handleDismiss}>
      <Card onClick={(e) => e.stopPropagation()}>
        <IconWrap>
          <Bell />
        </IconWrap>
        <Title>Enable notifications</Title>
        <Body>
          {tenantId
            ? 'Get rent due reminders, lease updates, and payment confirmations delivered to your device.'
            : 'Get payment alerts, trial reminders, and important updates delivered to your device.'}
        </Body>
        <Actions>
          <Button onClick={handleEnable} disabled={loading}>
            {loading ? 'Enabling…' : 'Enable notifications'}
          </Button>
          <Button variant="ghost" onClick={handleDismiss}>
            Not now
          </Button>
        </Actions>
      </Card>
    </Overlay>
  );
}

export default EnableNotificationsModal;
